/**
 * @module builder
 */
import type { OptionsDocument, PartnerContentFeatures, PresentationPageOption, PresentationSection } from 'paintscout'
import { createPresentationPage } from '../create-presentation-page'
import { getPrefabPages } from '../../../../database/fixtures/presentations/prefabPages'
import { getFeature } from '../../get-features'
import { ADDABLE_SHERWIN_WILLIAMS_PAGES } from '../sherwin-williams-section-types'
import { uuid } from '../../../util'

// Sherwin-Williams partner content (#21311): offered in the v2 editor as whole pages
// (user-facing "Sections") via the page-select dialog, NOT as individual sections. Each page
// wraps a single static partner section (the v1 components, reused by v2 — see
// react-quote PresentationNext/sections/partners/).
export function getSherwinWilliamsPageOptions(): PresentationPageOption[] {
  return ADDABLE_SHERWIN_WILLIAMS_PAGES.map(({ type, label }) => ({
    type,
    label,
    title: label,
    active: true,
    categories: ['sherwin-williams'],
    advanced: {
      useForNav: true,
      allowCustom: false
    },
    sections: [
      {
        key: uuid(),
        type,
        label,
        title: label,
        active: true,
        advanced: {
          useForNav: true,
          allowCustom: false
        }
      }
    ]
  }))
}

export function getPresentationPageOptions(args: {
  options?: OptionsDocument
  type?: string
  isNewPresentation?: boolean
}): PresentationPageOption[] {
  const { type, options, isNewPresentation } = args
  const hasSavedPages = options?.options?.presentationPageLibrary?.order?.length >= 1
  let defaultPageOptions: PresentationPageOption[] = [
    {
      type: 'empty',
      label: 'Empty Page',
      title: 'New Page',
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: true
      },
      sections: [] as PresentationSection[]
    },
    {
      type: 'quote',
      label: 'Quote',
      title: 'Quote',
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: true
      },
      sections: [
        {
          key: uuid(),
          type: 'quote',
          label: 'Quote',
          title: 'Quote',
          active: true,
          advanced: {
            useForNav: true,
            allowCustom: false
          },
          background: {
            style: 'contained',
            color: '#fff'
          }
        },
        {
          key: uuid(),
          type: 'pictures',
          label: 'Pictures',
          title: 'Pictures',
          active: true,
          advanced: {
            useForNav: true,
            allowCustom: false
          },
          background: {
            style: 'contained',
            color: '#fff'
          }
        },
        {
          key: uuid(),
          type: 'terms',
          label: 'Terms',
          title: 'Terms',
          active: true,
          advanced: {
            useForNav: true,
            allowCustom: false
          },
          background: {
            style: 'contained',
            color: '#fff'
          }
        }
      ]
    }
  ]

  if (isNewPresentation) {
    defaultPageOptions = getPrefabPages()

    const partnerContent: PartnerContentFeatures = getFeature({ options, path: 'partnerContent' })
    if (partnerContent?.enabled && partnerContent?.sherwinWilliams?.presentationPages?.enabled) {
      defaultPageOptions = [...defaultPageOptions, ...getSherwinWilliamsPageOptions()]
    }
  }

  if (hasSavedPages) {
    const savedPageOptions = options.options.presentationPageLibrary.order.map((page) => {
      return options.options.presentationPageLibrary.values[page]
    })

    const savedPages = savedPageOptions.map((page) => {
      return createPresentationPage({ presentationPageOption: page })
    })

    const response = [...defaultPageOptions, ...savedPages]

    if (type) {
      return response.filter((page) => page.type === type)
    }
    return response
  }
  if (type) {
    return defaultPageOptions.filter((option) => option.type === type)
  }

  return defaultPageOptions
}
